import { Car, UtensilsCrossed, Package, User, ArrowRight, Clock, Gift, Phone, Heart } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";
import { Button } from "@/components/ui/button";
import { IndexPageSkeleton } from "@/components/skeletons/IndexSkeleton";
import { LazyMagasMap, MapSkeleton } from "@/components/lazy";
import { ScrollReveal } from "@/components/ScrollReveal";
import { InstallBanner } from "@/components/InstallBanner";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState, useMemo, Suspense } from "react";
import { cn } from "@/lib/utils";

const Index = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, loading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);

  const getGreeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return t.goodMorning || "Доброе утро";
    if (hour < 18) return t.goodAfternoon || "Добрый день";
    return t.goodEvening || "Добрый вечер";
  }, [t]);

  const services = useMemo(() => [
    {
      icon: Car,
      title: t.taxi,
      description: t.taxiDesc || "Комфортные поездки по городу",
      color: "ios-blue",
      bgClass: "bg-ios-blue/10 dark:bg-ios-blue/20",
      iconBg: "bg-ios-blue",
      onClick: () => navigate("/taxi")
    },
    {
      icon: UtensilsCrossed,
      title: t.food,
      description: t.foodDesc || "Доставка еды из ресторанов",
      color: "ios-orange",
      bgClass: "bg-ios-orange/10 dark:bg-ios-orange/20",
      iconBg: "bg-ios-orange",
      onClick: () => navigate("/food")
    },
    {
      icon: Package,
      title: t.delivery,
      description: t.deliveryDesc || "Курьерская доставка",
      color: "ios-purple",
      bgClass: "bg-ios-purple/10 dark:bg-ios-purple/20",
      iconBg: "bg-ios-purple",
      onClick: () => navigate("/delivery")
    }
  ], [t, navigate]);

  const quickActions = useMemo(() => [
    { icon: Clock, label: t.history || "История", color: "ios-teal", onClick: () => navigate(user ? "/history" : "/auth") },
    { icon: Gift, label: t.bonuses || "Бонусы", color: "ios-yellow", onClick: () => navigate(user ? "/profile" : "/auth") },
    { icon: Phone, label: t.support || "Поддержка", color: "ios-green", onClick: () => navigate("/support") },
    { icon: Heart, label: "Избранное", color: "ios-pink", onClick: () => navigate(user ? "/favorites" : "/auth") }
  ], [t, user, navigate]);

  const features = useMemo(() => [
    { emoji: "🥘", label: t.halal || "Халяль", desc: "Сертифицированные рестораны" },
    { emoji: "👩", label: t.femaleDriver || "Для женщин", desc: "Женщины-водители" },
    { emoji: "💵", label: t.cashPayment || "Наличные", desc: "Оплата как удобно" }
  ], [t]);

  return (
    <div className="min-h-screen pb-24">
      {/* Header with glassmorphism */}
      <header className="fixed top-0 left-0 right-0 z-50 ios-glass border-b border-border/30">
        <div className="max-w-2xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 group cursor-pointer">
            <img 
              src="/favicon.png" 
              alt="Новкъаст GO" 
              className="w-8 h-8 rounded-lg transition-all duration-300 group-hover:scale-110 group-hover:shadow-[0_0_20px_hsl(160_84%_50%/0.5)]"
            />
            <span className="text-lg font-semibold tracking-tight bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent transition-all duration-300 group-hover:from-emerald-400 group-hover:to-teal-400">
              Новкъаст GO
            </span>
          </div>
          <div className="flex items-center gap-1">
            <ThemeSwitcher />
            <LanguageSwitcher />
            {user ? (
              <Button
                onClick={() => navigate("/profile")}
                size="icon"
                variant="ghost"
                className="rounded-full w-9 h-9 hover:bg-primary/10"
              >
                <User className="h-5 w-5" />
              </Button>
            ) : (
              <Button
                onClick={() => navigate("/auth")}
                variant="ghost"
                className="text-sm font-medium text-primary hover:bg-primary/10"
              >
                {t.login}
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="h-14" />

      <main className="max-w-2xl mx-auto px-5">
        {isLoading || authLoading ? (
          <IndexPageSkeleton />
        ) : (
          <div className="py-8 space-y-10">
            {/* Install Banner */}
            <InstallBanner />
            {/* Hero Section */}
            <ScrollReveal animation="fade-up">
              <section className="space-y-3">
                <p className="text-sm font-medium text-primary">{getGreeting}</p>
                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
                  {user ? (
                    <>Привет, <span className="text-primary">{user.email?.split('@')[0]}</span> 👋</>
                  ) : (
                    <>Добро пожаловать в <span className="bg-gradient-to-r from-ios-blue via-ios-purple to-ios-pink bg-clip-text text-transparent">Ингушетию</span></>
                  )}
                </h1>
                <p className="text-base text-muted-foreground">
                  Такси, еда и доставка — всё в одном приложении
                </p>
              </section>
            </ScrollReveal>

            {/* Services Grid */}
            <ScrollReveal animation="fade-up" delay={100}>
              <section className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Сервисы
                  </h2>
                </div>
                <div className="space-y-3">
                  {services.map((service, index) => {
                    const Icon = service.icon;
                    return (
                      <button
                        key={index}
                        onClick={service.onClick}
                        className={cn(
                          "w-full group relative overflow-hidden rounded-2xl p-5 text-left",
                          "transition-all duration-300 ease-out",
                          "hover:scale-[1.02] active:scale-[0.98]",
                          "border border-border/50 hover:border-border",
                          "bg-card hover:shadow-lg",
                          "ios-press"
                        )}
                      >
                        <div className="flex items-center gap-4">
                          <div className={cn(
                            "w-14 h-14 rounded-2xl flex items-center justify-center",
                            "shadow-lg transition-transform duration-300 group-hover:scale-110",
                            service.iconBg
                          )}>
                            <Icon className="h-7 w-7 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold">{service.title}</h3>
                            <p className="text-sm text-muted-foreground truncate">{service.description}</p>
                          </div>
                          <div className="w-10 h-10 rounded-full bg-secondary/80 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            </ScrollReveal>

            {/* Features Cards */}
            <ScrollReveal animation="fade-up" delay={200}>
              <section className="space-y-4">
                <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Особенности региона
                </h2>
                <div className="grid grid-cols-3 gap-3">
                  {features.map((item, index) => (
                    <div 
                      key={index} 
                      className={cn(
                        "relative rounded-2xl p-4 text-center",
                        "bg-card border border-border/50",
                        "hover:border-border hover:shadow-md transition-all duration-200"
                      )}
                    >
                      <span className="text-3xl mb-2 block">{item.emoji}</span>
                      <p className="text-sm font-semibold mb-0.5">{item.label}</p>
                      <p className="text-[10px] text-muted-foreground leading-tight">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>
            </ScrollReveal>

            {/* Quick Actions */}
            <ScrollReveal animation="fade-up" delay={300}>
              <section className="space-y-4">
                <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Быстрый доступ
                </h2>
                <div className="grid grid-cols-4 gap-2">
                  {quickActions.map((action, index) => {
                    const Icon = action.icon;
                    const colorClasses: Record<string, string> = {
                      'ios-teal': 'bg-ios-teal/15 text-ios-teal hover:bg-ios-teal/25',
                      'ios-yellow': 'bg-ios-yellow/15 text-ios-yellow hover:bg-ios-yellow/25',
                      'ios-green': 'bg-ios-green/15 text-ios-green hover:bg-ios-green/25',
                      'ios-pink': 'bg-ios-pink/15 text-ios-pink hover:bg-ios-pink/25',
                    };
                    return (
                      <button
                        key={index}
                        onClick={action.onClick}
                        className={cn(
                          "flex flex-col items-center gap-2 p-3 rounded-2xl",
                          "bg-card border border-border/50",
                          "hover:border-border transition-all duration-200",
                          "active:scale-95"
                        )}
                      >
                        <div className={cn(
                          "w-11 h-11 rounded-xl flex items-center justify-center transition-colors",
                          colorClasses[action.color]
                        )}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <span className="text-[11px] font-medium text-center leading-tight">{action.label}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            </ScrollReveal>

            {/* Map Section */}
            <ScrollReveal animation="fade-up" delay={400}>
              <section className="space-y-4">
                <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Города Ингушетии
                </h2>
                <div className="rounded-2xl overflow-hidden border border-border/50 shadow-sm">
                  <Suspense fallback={<MapSkeleton className="h-80" />}>
                    <LazyMagasMap />
                  </Suspense>
                </div>
              </section>
            </ScrollReveal>

            {/* CTA for non-logged users */}
            {!user && (
              <ScrollReveal animation="fade-up" delay={500}>
                <section className="rounded-2xl border border-border/50 bg-card p-6 text-center space-y-4">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center">
                    <Package className="w-7 h-7 text-primary" />
                  </div>
                  <div className="space-y-2">
                    <h2 className="text-xl font-semibold">Начните прямо сейчас</h2>
                    <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                      Создайте аккаунт для заказов, накопления бонусов и персональных предложений
                    </p>
                  </div>
                  <Button
                    onClick={() => navigate("/auth")}
                    size="lg"
                    className="rounded-full px-8"
                  >
                    Создать аккаунт
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </section>
              </ScrollReveal>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Index;
